/**
 * PigTestRing — Minimal combat testing map.
 *
 * Single ground platform, one pig mob, instant respawn.
 * Used as default map during QA testing.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { MAP_DEFAULTS } from '../../config/map/defaults.config';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

const MAP_ID = 'pig-testring';
const MAP_NAME = 'Pig Test Ring';
const VERSION = '1.0';

const W = MAP_DEFAULTS.widthTiles;
const H = MAP_DEFAULTS.heightTiles;
const T = MAP_DEFAULTS.tileSize;

// Layers (empty — flat ground only via platforms)
const groundLayer = new Map<string, number>();
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

// Ground at y=700 (low on screen), spawn + pig just above it
const GROUND_Y = 700;

// pig hitbox height — must match entities.gen.ts pig.height (60×50).
// entity.y is the TOP of the hitbox; see OneVersusOnePigMap.ts:28-32 for the
// convention citation. The player's y - 40 spawn pattern below works ONLY
// because the player happens to be exactly 40×40 — do NOT copy that pattern
// for any other entity type.
const PIG_HEIGHT = 50;

const entities: any[] = [
  { type: 'pig', id: 'pig_test_1', x: 400,  y: GROUND_Y - PIG_HEIGHT, properties: { displayName: 'Wild Pig', respawnTime: 1000 } },
  { type: 'pig', id: 'pig_test_2', x: 550,  y: GROUND_Y - PIG_HEIGHT, properties: { displayName: 'Wild Pig', respawnTime: 1000 } },
  { type: 'pig', id: 'pig_test_3', x: 700,  y: GROUND_Y - PIG_HEIGHT, properties: { displayName: 'Wild Pig', respawnTime: 1000 } },
  { type: 'pig', id: 'pig_test_4', x: 850,  y: GROUND_Y - PIG_HEIGHT, properties: { displayName: 'Wild Pig', respawnTime: 1000 } },
  { type: 'pig', id: 'pig_test_5', x: 1000, y: GROUND_Y - PIG_HEIGHT, properties: { displayName: 'Wild Pig', respawnTime: 1000 } },
];

const spawnPoints = [{ x: 10, y: Math.floor((GROUND_Y - 40) / T) }];
const defaultSpawn = { x: 400, y: GROUND_Y - 40 };

const runtimePlatforms = [
  {
    id: 'ground-floor',
    x: 0,
    y: GROUND_Y,
    width: W * T,
    height: MAP_DEFAULTS.ground.height,
    type: MAP_DEFAULTS.ground.type,
    color: MAP_DEFAULTS.ground.color,
  },
];

export const PigTestRing: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,
  backgroundColor: MAP_DEFAULTS.backgroundColor,
  gravity: MAP_DEFAULTS.gravity,
  spawnPoints,
  defaultSpawn,
  layers: [
    { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: groundLayer },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionLayer },
    { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: objectsLayer },
    { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: entitiesLayer, entities },
    { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: triggersLayer },
  ],
  platforms: runtimePlatforms,
  entities,
  spawnRules: [],
  type: 'field',
  metadata: {
    author: 'DJ',
    description: 'Combat testing arena. 5 pigs, instant respawn, aggro visualization.',
    tags: ['testing', 'combat'],
    // PvE-default — pig pile-on, no PvP. Aggro + respawn pacing for
    // combat verification.
    profile: 'pve_default',
  },
};
